import React from 'react';
import data from '../data.json';

export default function Hero() {
  const { company, stats } = data;

  return (
    <section id="hero" style={{
      minHeight: '100vh', position: 'relative',
      display: 'flex', alignItems: 'center',
      overflow: 'hidden', paddingTop: 70,
    }}>
      {/* Background gradient mesh */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          radial-gradient(ellipse 75% 65% at 72% 45%, rgba(0,194,168,0.12) 0%, transparent 58%),
          radial-gradient(ellipse 55% 55% at 12% 78%, rgba(255,107,43,0.14) 0%, transparent 55%),
          radial-gradient(ellipse 40% 35% at 50% 8%,  rgba(255,215,0,0.05)  0%, transparent 50%),
          linear-gradient(160deg, #06060F 0%, #0A0A1C 45%, #080820 100%)
        `,
      }} />

      {/* Subtle animated grid */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.025,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,.9) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.9) 1px, transparent 1px)
        `,
        backgroundSize: '44px 44px',
        animation: 'gridMove 20s linear infinite',
      }} />

      {/* Alpona-inspired decorative rings */}
      {[
        { size: 580, color: 'rgba(255,107,43,.13)', top: -140, right: -140, dur: '40s' },
        { size: 290, color: 'rgba(0,194,168,.1)',   top: -40,  right: -40,  dur: '28s', rev: true },
        { size: 190, color: 'rgba(255,215,0,.08)',  bottom: 90, right: '20%', dur: '22s' },
      ].map((r, i) => (
        <div key={i} style={{
          position: 'absolute', width: r.size, height: r.size,
          borderRadius: '50%', border: `1px solid ${r.color}`,
          top: r.top, right: r.right, bottom: r.bottom,
          opacity: 0.9, pointerEvents: 'none',
          animation: `rotateSlow ${r.dur} linear infinite ${r.rev ? 'reverse' : ''}`,
        }} />
      ))}

      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ maxWidth: 730, padding: '80px 0' }}>

          {/* Badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: 'rgba(255,107,43,0.1)', border: '1px solid rgba(255,107,43,0.28)',
            borderRadius: 100, padding: '6px 18px', marginBottom: 30,
            animation: 'fadeUp 0.6s ease both',
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)',
              animation: 'pulseRing 2s infinite', display: 'block', flexShrink: 0,
            }} />
            <span style={{
              fontSize: 11.5, fontWeight: 700, color: 'var(--accent)',
              letterSpacing: '1.5px', textTransform: 'uppercase',
            }}>
              Kolkata's Bold Digital Marketing Startup
            </span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontSize: 'clamp(2.5rem, 6vw, 4.6rem)',
            fontWeight: 900, lineHeight: 1.05, marginBottom: 24,
            letterSpacing: '-0.03em',
            animation: 'fadeUp 0.7s 0.1s ease both',
          }}>
            We Turn Brands Into{' '}
            <span style={{
              background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent3) 60%, var(--accent2) 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              Digital Legends
            </span>
          </h1>

          <p style={{
            fontSize: '1.08rem', color: 'var(--gray-400)',
            maxWidth: 540, lineHeight: 1.8, marginBottom: 38,
            animation: 'fadeUp 0.7s 0.2s ease both',
          }}>
            {company.description}
          </p>

          <div style={{
            display: 'flex', gap: 14, flexWrap: 'wrap',
            animation: 'fadeUp 0.7s 0.3s ease both',
          }}>
            <a href="#contact" className="btn-primary" style={{ fontSize: 15, padding: '15px 34px' }}>
              Start Growing →
            </a>
            <a href="#services" className="btn-secondary" style={{ fontSize: 15, padding: '15px 34px' }}>
              Our Services
            </a>
          </div>

          {/* Kolkata tagline */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 12, color: 'var(--gray-500)', marginTop: 28,
            fontWeight: 500, animation: 'fadeUp 0.7s 0.4s ease both',
          }}>
            🌸 City of Joy — Serving the World
          </div>

          {/* Stats — honest startup values */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
            gap: 22, marginTop: 60, paddingTop: 36,
            borderTop: '1px solid var(--border)',
            animation: 'fadeUp 0.7s 0.5s ease both',
          }} className="stats-grid">
            {stats.map((stat, i) => (
              <div key={i}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>{stat.icon}</div>
                <div style={{
                  fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontWeight: 900,
                  letterSpacing: '-0.03em',
                  background: 'linear-gradient(135deg, #fff 40%, var(--accent2))',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4, fontWeight: 500 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* Startup honesty note */}
          <div style={{
            marginTop: 28, padding: '14px 20px',
            background: 'rgba(0,194,168,0.07)',
            border: '1px solid rgba(0,194,168,0.18)',
            borderRadius: 8,
            fontSize: 13, color: 'var(--gray-400)',
            display: 'flex', alignItems: 'center', gap: 10,
            animation: 'fadeUp 0.7s 0.6s ease both',
            maxWidth: 540,
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>💬</span>
            <span>
              <strong style={{ color: 'var(--accent2)' }}>We're a new startup</strong> — fresh, ambitious and fully committed to making your brand our proof of excellence.
            </span>
          </div>

        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .stats-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>
    </section>
  );
}
